import { Skeleton } from '@/components/ui-kit/skeleton';

export type ReportSkeletonVariant = 'overview' | 'deliverability' | 'campaign' | 'links';

export function ReportSkeleton({ variant }: { variant: ReportSkeletonVariant }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-5">
      <span className="sr-only">Loading report</span>
      {variant === 'overview' ? <OverviewSkeleton /> : null}
      {variant === 'deliverability' ? <DeliverabilitySkeleton /> : null}
      {variant === 'campaign' ? <CampaignSkeleton /> : null}
      {variant === 'links' ? <LinksSkeleton /> : null}
    </div>
  );
}

function KpiCard() {
  return (
    <div className="rounded-xl border border-[#E7E8EA] bg-white p-4">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-7 w-16" />
      <Skeleton className="mt-3 h-3 w-36" />
    </div>
  );
}

function TableCard({ columns, rows = 6 }: { columns: number; rows?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-200 p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-2 h-3 w-64" />
      </div>
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-4">
            {Array.from({ length: columns }, (_, column) => (
              <Skeleton key={column} className={column === 0 ? 'h-3.5 flex-1' : 'h-3.5 w-16'} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard />
        <KpiCard />
        <KpiCard />
        <KpiCard />
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="mt-2 h-3 w-72" />
        <Skeleton className="mt-4 h-[220px] w-full" />
      </div>
      <TableCard columns={5} />
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <Skeleton className="h-4 w-32" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </>
  );
}

function DeliverabilitySkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard />
        <KpiCard />
        <KpiCard />
      </div>
      <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-2.5 w-full rounded-full" />
        <Skeleton className="h-2.5 w-full rounded-full" />
      </div>
      <TableCard columns={6} />
    </>
  );
}

function CampaignSkeleton() {
  return (
    <>
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-2 h-3 w-40" />
        <div className="mt-4 flex gap-4 border-t border-neutral-100 pt-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="mt-4 h-20 w-full" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <KpiCard />
        <KpiCard />
        <KpiCard />
        <KpiCard />
      </div>
    </>
  );
}

function LinksSkeleton() {
  return <TableCard columns={4} />;
}

export function EventRowsSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <tr key={index} className="border-t border-neutral-100">
          <td className="px-4 py-2.5">
            <Skeleton className="h-5 w-24 rounded-full" />
          </td>
          <td className="px-4 py-2.5">
            <Skeleton className="h-3.5 w-40" />
          </td>
          <td className="px-4 py-2.5">
            <Skeleton className="h-3.5 w-48" />
          </td>
          <td className="px-4 py-2.5">
            <Skeleton className="ml-auto h-3.5 w-20" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function EmailPreviewSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm"
    >
      <span className="sr-only">Loading the sent email</span>
      <Skeleton className="h-[420px] w-full" />
    </div>
  );
}
